const CENTRAL_TIME = 'America/Chicago';
const HOUR_MS = 60 * 60 * 1000;

export const REVIEW_TARGET_HOURS = 24;
export const RESOLUTION_NOTE_MAX = 2000;

export const STATUS_FILTERS = [
  { value: 'unresolved', label: 'Needs review' },
  { value: 'open', label: 'Open' },
  { value: 'reviewing', label: 'In review' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'all', label: 'All' },
];

export const RESOLUTIONS = [
  { value: 'no_violation', label: 'No violation', detail: 'Nothing needed. The content stays.' },
  { value: 'content_removed', label: 'Content removed', detail: 'You removed the reported words.' },
  { value: 'member_warned', label: 'Member warned', detail: 'You or the church spoke with the member.' },
  { value: 'member_removed', label: 'Member removed', detail: 'The member was removed from the church.' },
  { value: 'account_disabled', label: 'Account disabled', detail: 'The account was suspended.' },
];

const STATUS_LABELS = { open: 'Open', reviewing: 'In review', resolved: 'Resolved' };

const KIND_LABELS = {
  prayer: 'Prayer request',
  prayer_comment: 'Prayer comment',
  chat_message: 'Chat message',
  announcement_comment: 'Announcement comment',
  person: 'Person',
};

const REASON_LABELS = {
  unspecified: 'No reason given',
  spam: 'Spam',
  harassment: 'Harassment',
  hate: 'Hate',
  sexual_content: 'Sexual content',
  violence: 'Violence',
  self_harm: 'Self-harm',
  impersonation: 'Impersonation',
  other: 'Other',
};

export function statusFilterFrom(search) {
  const value = new URLSearchParams(search).get('status');
  return STATUS_FILTERS.some((filter) => filter.value === value) ? value : 'unresolved';
}

export function hoursOpen(createdAt, now = Date.now()) {
  if (!createdAt) return null;
  const filed = new Date(createdAt).getTime();
  if (Number.isNaN(filed)) return null;
  return Math.max(0, (now - filed) / HOUR_MS);
}

/** Past the 24 hour review target and still waiting on a resolution. */
export function isOverdue(report, now = Date.now()) {
  if (report?.status === 'resolved') return false;
  const hours = hoursOpen(report?.createdAt, now);
  return hours !== null && hours >= REVIEW_TARGET_HOURS;
}

export function waitingLabel(createdAt, now = Date.now()) {
  const hours = hoursOpen(createdAt, now);
  if (hours === null) return '—';
  if (hours < 1) return 'under an hour';
  if (hours < 48) return `${Math.floor(hours)} h`;
  return `${Math.floor(hours / 24)} days`;
}

export function formatCentral(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleString('en-US', {
    timeZone: CENTRAL_TIME,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} CT`;
}

export function statusLabel(status) {
  return STATUS_LABELS[status] ?? status ?? '—';
}

export function kindLabel(kind) {
  return KIND_LABELS[kind] ?? kind ?? '—';
}

export function reasonLabel(reason) {
  return REASON_LABELS[reason] ?? reason ?? '—';
}

export function resolutionLabel(resolution) {
  return RESOLUTIONS.find((option) => option.value === resolution)?.label ?? resolution ?? '—';
}

/**
 * @returns {{ ok: true, body: { resolution: string, note: string } }
 *   | { ok: false, errors: { resolution?: string, note?: string } }}
 */
export function checkResolution({ resolution, note }) {
  const errors = {};
  if (!RESOLUTIONS.some((option) => option.value === resolution)) {
    errors.resolution = 'Choose a resolution.';
  }
  const trimmed = typeof note === 'string' ? note.trim() : '';
  if (!trimmed) {
    errors.note = 'Write a note. It stays with the report.';
  } else if (trimmed.length > RESOLUTION_NOTE_MAX) {
    errors.note = `Keep the note under ${RESOLUTION_NOTE_MAX} characters.`;
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, body: { resolution, note: trimmed } };
}
